// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, Loading } from '~/components/common';
// Others
import { IFormNote } from '~/utils/interface/note';
import { createNote } from '~/thunks/note/noteSlice';
import { noteActions } from '~/thunks/note/noteThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { removeEmptyObjects } from '~/utils/helper/common';
import { defaultValuesNoteForm, schema } from './helper';
import { BusinessTypeEnum, JobTabEnum, NoteTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './FormNote.module.scss';
import { icons } from '~/assets';

type Props = {
  type: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormNote = (props: Props) => {
  //#region Destructuring Props
  const { type, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const { id: businessId } = useParams();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormNote>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesNoteForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const convertTypeNote = () => {
    switch (type) {
      case JobTabEnum.INTERNAL_NOTES:
        return NoteTypeEnum.INTERNAL;

      case JobTabEnum.EXTERNAL_NOTES:
        return NoteTypeEnum.EXTERNAL;

      case JobTabEnum.SITE_NOTES:
        return NoteTypeEnum.SITE;
    }
  };

  const handleFormNote = (data: IFormNote) => {
    if (!data) return;

    setIsLoading(true);

    const convertData = removeEmptyObjects({
      ...data,
      businessId: businessId,
      businessType: BusinessTypeEnum.JOB,
      type: convertTypeNote(),
    }) as IFormNote;

    dispatch(createNote(convertData))
      .unwrap()
      .then(() => {
        dispatch(noteActions.setRefreshNoteList(true));
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='noteFormModalComponent'
      aria-labelledby='noteFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='noteFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('note_form_create_title')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Stack className={cx('viewInput')}>
          <Controller
            control={control}
            name={'content'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  multiline
                  rows={7}
                  height={'auto'}
                  label={t('note_form_note_title')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.content?.message}
                  required
                />
              );
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleFormNote)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default FormNote;
